import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { careerAPI } from '../../../services/careerApi';
import { CareerStatus } from '../../../types/career';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ErrorState from '../../../components/ui/ErrorState';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { usePageTitle } from '../../../hooks/usePageTitle';

export default function CareerEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [sponsor, setSponsor] = useState('');
  const [location, setLocation] = useState('');
  const [summary, setSummary] = useState('');
  const [applyUrl, setApplyUrl] = useState('');
  const [status, setStatus] = useState<CareerStatus>('DRAFT');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  usePageTitle(isNew ? 'New posting' : 'Edit posting');

  useEffect(() => {
    if (!id) return;
    careerAPI
      .fetchPosting(id)
      .then((posting) => {
        setTitle(posting.title);
        setSponsor(posting.sponsor);
        setLocation(posting.location ?? '');
        setSummary(posting.summary ?? '');
        setApplyUrl(posting.applyUrl);
        setStatus(posting.status);
      })
      .catch((err) => setError(err instanceof Error ? err : new Error('Failed to load posting')))
      .finally(() => setLoading(false));
  }, [id]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setFormError(null);
    const input = { title, sponsor, location, summary, applyUrl, status };
    try {
      if (isNew) {
        await careerAPI.createPosting(input);
      } else if (id) {
        await careerAPI.updatePosting(id, input);
      }
      navigate('/admin/careers');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save posting');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState error={error} title="Posting not found" onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'Careers', to: '/admin/careers' },
          { label: isNew ? 'New posting' : title || 'Edit posting' },
        ]}
      />
      <Link to="/admin/careers" className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-6">
        <ArrowLeftIcon className="h-5 w-5" />
        <span>Back to careers</span>
      </Link>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">{isNew ? 'New posting' : 'Edit posting'}</h1>
      <form onSubmit={onSubmit} className="space-y-4">
        <Input label="Title" value={title} onChange={(event) => setTitle(event.target.value)} required />
        <Input
          label="Office or sponsor"
          value={sponsor}
          onChange={(event) => setSponsor(event.target.value)}
          required
        />
        <p className="text-sm text-gray-600 -mt-2">The organization that is hiring. Apply links go to their site.</p>
        <Input label="Location" value={location} onChange={(event) => setLocation(event.target.value)} />
        <div>
          <label htmlFor="career-summary" className="block text-sm font-medium text-gray-700 mb-2">
            Summary
          </label>
          <textarea
            id="career-summary"
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
            rows={6}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:border-primary focus:ring-2 focus:ring-primary"
          />
        </div>
        <Input
          label="Apply link"
          type="url"
          value={applyUrl}
          onChange={(event) => setApplyUrl(event.target.value)}
          placeholder="https://"
          required
        />
        <Select
          label="Status"
          value={status}
          onChange={(event) => setStatus(event.target.value as CareerStatus)}
          options={[
            { value: 'DRAFT', label: 'Draft' },
            { value: 'PUBLISHED', label: 'Published' },
          ]}
        />
        {formError && <p className="text-sm text-error">{formError}</p>}
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save posting'}
        </Button>
      </form>
    </div>
  );
}
